import { environment } from 'environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { from } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CurrencyCode } from '../../models/payment.interfaces';

const API_KEY = environment.paysafe;

const THREE_DS_CONFIG = {
  merchantUrl: 'https://www.paysafe.com',
  deviceChannel: 'BROWSER',
  messageCategory: 'PAYMENT',
  transactionIntent: 'GOODS_OR_SERVICE_PURCHASE',
  authenticationPurpose: 'PAYMENT_TRANSACTION'
};

export interface PaysafePayment {
  fromAccount: string | null;
  toAccount: string | null;
  beneficiaryName: string;
  transferAmount: number;
  purpose: string;
  address?: {
    country?: string;
    state?: string;
    city?: string;
    zipCode?: string;
    street?: string;
    apartment?: string;
  };
}

export interface MpnolitPaysafePayment {
  paymentPaysafe: {
    fromAccount: string | null; // 'MT80PAPY36836000002676370018326',
    toAccount: string | null;
    beneficiaryName: string;
    purpose: string | 'top up card';
    amount: {
      sum: {
        currency: {
          label: 'string';
          code: CurrencyCode;
        },
        value: number;
      }
    },
    paymentHandleToken: string;
  };
}

const CONFIRMATION_TYPE = environment.bankSettings.transactionConfirmationType;

export interface PaysafePaymentCommission {
  currency: { 'code': 'EUR' };
  fee: number;
  amount: number;
  totalAmount: number;
}

@Injectable({
  providedIn: 'root'
})
export class PaysafeService {
  defaultOptions = {
    currencyCode: 'EUR',
    environment: 'TEST',
    accounts: {
      default: 1002782370
    },
    fields: {
      cardNumber: {
        selector: '#card-number',
        placeholder: '****************',
        separator: ' ',
      },
      cvv: {
        selector: '#cvv',
        placeholder: '****',
        mask: false,
      },
      expiryDate: {
        selector: '#expiry-date',
        placeholder: '**/**',
      }
    },
  };

  instance!: any;
  activePaymentData!: MpnolitPaysafePayment;

  static loadScript(src, callback) {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => callback(null, script);
    script.onerror = () => callback(new Error(`Ошибка загрузки скрипта ${src}`));
    document.head.appendChild(script);
  }

  static generateGuid() {
    return (([1e7] as any) + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
        (
            c ^
            // tslint:disable-next-line:no-bitwise
            (window.crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
        ).toString(16)
    );
  }

  constructor(private http: HttpClient) {}

  loadPaysafeSource() {
    PaysafeService.loadScript('https://hosted.paysafe.com/js/v1/latest/paysafe.min.js', (err, script) => {
      if (!err) {
        this.setup(API_KEY, this.defaultOptions);
      }
    });
  }

  async setup(apiKey, options) {
    (window as any).paysafe.fields.setup(apiKey, options)
        .then(inst => {
          this.instance = inst;
          return this.instance.show();
        });
  }

  private parseFullName(fullName: string): { firstName: string; lastName: string } {
    const nameParts = fullName.trim().split(' ');
    return {
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || ''
    };
  }

  private createCustomerProfile(clientData: { name: string; email: string; phone: string; } | undefined, merchantCustomerId: string) {
    if (!clientData) {
      return undefined;
    }

    const { firstName, lastName } = this.parseFullName(clientData.name);
    return {
      firstName,
      lastName,
      email: clientData.email,
      phone: clientData.phone,
      merchantCustomerId
    };
  }

  private createBillingDetails(address: PaysafePayment['address']) {
    return {
      country: address?.country,
      state: address?.state,
      city: address?.city,
      zip: address?.zipCode,
      street: address?.street
    };
  }

  tokenize(
    rawAmount: number,
    holderName: string,
    clientData?: { name: string; email: string; phone: string; },
    address?: PaysafePayment['address']
  ): Promise<{ token: string }> {
    const scaledAmount = Math.round(rawAmount * 100);
    const guid = PaysafeService.generateGuid();

    const tokenizationOptions = {
      amount: scaledAmount,
      merchantRefNum: guid,
      transactionType: 'STANDALONE_CREDIT',
      paymentType: 'CARD',
      openAs: 'IFRAME',
      customerDetails: {
        holderName,
        profile: this.createCustomerProfile(clientData, guid),
        billingDetails: this.createBillingDetails(address)
      },
      threeDs: THREE_DS_CONFIG
    };

    return this.instance.tokenize(tokenizationOptions);
  }

  paymentTokenize(model: PaysafePayment, clientData?: { name: string; email: string; phone: string; }) {
    return from(this.tokenize(model.transferAmount, model.beneficiaryName, clientData, model.address)).pipe(
        tap(response => {
          this.activePaymentData = {
            paymentPaysafe: {
              fromAccount: model.fromAccount || null,
              toAccount: model.toAccount || null,
              beneficiaryName: model.beneficiaryName,
              purpose: model.purpose || 'card to card',
              amount: {
                sum: {
                  currency: {
                    label: 'string',
                    code: 'EUR'
                  },
                  value: model.transferAmount,
                }
              },
              paymentHandleToken: response.token,
            }
          };
        })
    );
  }

  requestPaymentCommission() {
    return this.http.post<PaysafePaymentCommission>(environment.baseApi + '/v1/payment_paysafe/commission', this.activePaymentData);
  }


  requestPaysafePayment() {
    return this.requestMonolitPaysafePayment(this.activePaymentData);
  }

  requestMonolitPaysafePayment(model: MpnolitPaysafePayment) {
    const headers = new HttpHeaders({
      'X-Confirmation-Type': CONFIRMATION_TYPE
    });
    return this.http.post<string>(environment.baseApi + '/v1/payment_paysafe', model, { headers });
  }

  confirmMonolitPaysafePayment(headers: HttpHeaders) {
    return this.http.post<string>(environment.baseApi + '/v1/payment_paysafe', this.activePaymentData, { headers }).pipe(
        tap(() => {
          this.activePaymentData = null;
        })
    );
  }
}
